import { ParsedCoordinatePair } from '../models/coordinate-search.model';

const LAT_LETTERS = 'NnSs';
const LNG_LETTERS = 'EeWw';

interface CardinalComponent {
  value: number;
  hemisphere: 'N' | 'S' | 'E' | 'W';
  style: 'prefix' | 'suffix';
}

function parseCardinalComponent(token: string, kind: 'lat' | 'lng'): CardinalComponent | null {
  const t = token.trim();
  if (!t) {
    return null;
  }

  const letters = kind === 'lat' ? LAT_LETTERS : LNG_LETTERS;
  const prefix = new RegExp(`^([${letters}])\\s*([+-]?\\d+(?:\\.\\d+)?)$`, 'i');
  const suffix = new RegExp(`^([+-]?\\d+(?:\\.\\d+)?)\\s*([${letters}])$`, 'i');

  let match = t.match(prefix);
  if (match) {
    const value = Number(match[2]);
    if (!Number.isFinite(value) || value < 0) {
      return null;
    }
    if (String(match[2]).startsWith('-') || String(match[2]).startsWith('+') && match[2] !== String(value)) {
      // signed numeric with cardinal prefix is invalid (-45N style handled below)
    }
    if (/^-/.test(match[2])) {
      return null;
    }
    return {
      value: Math.abs(value),
      hemisphere: match[1].toUpperCase() as CardinalComponent['hemisphere'],
      style: 'prefix',
    };
  }

  match = t.match(suffix);
  if (match) {
    if (/^-/.test(match[1])) {
      return null;
    }
    const value = Number(match[1]);
    if (!Number.isFinite(value) || value < 0) {
      return null;
    }
    return {
      value: Math.abs(value),
      hemisphere: match[2].toUpperCase() as CardinalComponent['hemisphere'],
      style: 'suffix',
    };
  }

  return null;
}

function applyHemisphere(value: number, hemisphere: string, kind: 'lat' | 'lng'): number {
  if (kind === 'lat') {
    return hemisphere === 'S' ? -value : value;
  }
  return hemisphere === 'W' ? -value : value;
}

/**
 * Cardinal suffix: 45.5017N,73.5673W
 * Cardinal prefix: N45.5017,W73.5673
 * Mixed prefix/suffix across the pair is rejected.
 */
export function parseCardinalPair(input: string): ParsedCoordinatePair | null {
  const trimmed = input.trim();
  const parts = trimmed.split(/[\s,]+/).filter(Boolean);
  if (parts.length !== 2) {
    const commaSplit = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    if (commaSplit.length === 2) {
      return parseCardinalPair(`${commaSplit[0]} ${commaSplit[1]}`);
    }
    return null;
  }

  const latComp = parseCardinalComponent(parts[0], 'lat');
  const lngComp = parseCardinalComponent(parts[1], 'lng');
  if (!latComp || !lngComp) {
    return null;
  }

  if (latComp.style !== lngComp.style) {
    return null;
  }

  const lat = applyHemisphere(latComp.value, latComp.hemisphere, 'lat');
  const lng = applyHemisphere(lngComp.value, lngComp.hemisphere, 'lng');

  return {
    lat,
    lng,
    sourceFormat: 'cardinal',
    rawInput: input,
  };
}
